import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../api";
import { Mail, Trash2, MailOpen, Loader2, Reply } from "lucide-react";

export const AdminInbox = () => {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/submissions");
      setItems(data.items);
      if (!selected && data.items[0]) setSelected(data.items[0]);
    } catch (e) { setError(formatApiError(e)); }
    finally { setLoading(false); }
  }, [selected]);

  useEffect(() => { load(); }, []); // eslint-disable-line

  const toggleRead = async (item) => {
    const endpoint = item.read ? "unread" : "read";
    try {
      await api.post(`/admin/submissions/${item.id}/${endpoint}`);
      setItems((prev) => prev.map((x) => (x.id === item.id ? { ...x, read: !item.read } : x)));
      if (selected?.id === item.id) setSelected({ ...selected, read: !item.read });
    } catch (e) { setError(formatApiError(e)); }
  };

  const remove = async (item) => {
    if (!window.confirm(`Delete the enquiry from ${item.name}?`)) return;
    try {
      await api.delete(`/admin/submissions/${item.id}`);
      setItems((prev) => prev.filter((x) => x.id !== item.id));
      if (selected?.id === item.id) setSelected(null);
    } catch (e) { setError(formatApiError(e)); }
  };

  const openSelect = async (item) => {
    setSelected(item);
    if (!item.read) await toggleRead(item);
  };

  const fmt = (iso) => {
    try { return new Date(iso).toLocaleString("en-AU", { dateStyle: "medium", timeStyle: "short" }); }
    catch { return iso; }
  };

  return (
    <div className="max-w-5xl" data-testid="admin-inbox">
      <p className="sc-eyebrow">Inbox</p>
      <h1 className="font-serif text-3xl md:text-4xl text-ink mt-2">Enquiries</h1>
      <p className="sc-body mt-2">Messages sent via the contact form.</p>

      {error && <p className="text-magenta text-sm mt-4">{error}</p>}

      <div className="mt-8 grid grid-cols-1 md:grid-cols-[320px_1fr] gap-6 min-h-[500px]">
        {/* List */}
        <div className="bg-white border border-blush-200/70 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center p-10 text-muteink"><Loader2 className="animate-spin mr-2" size={16} /> Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center">
              <Mail className="mx-auto text-muteink/40 mb-3" size={28} />
              <p className="text-ink font-serif text-lg">No enquiries yet.</p>
              <p className="text-muteink text-[13px] mt-1">They will appear here when someone submits the form.</p>
            </div>
          ) : (
            <ul className="divide-y divide-blush-200/60 max-h-[70vh] overflow-y-auto" data-testid="inbox-list">
              {items.map((it) => (
                <li key={it.id}>
                  <button onClick={() => openSelect(it)}
                          className={`w-full text-left px-4 py-3.5 transition-colors ${selected?.id === it.id ? "bg-blush-50" : "hover:bg-blush-50/60"}`}
                          data-testid={`inbox-item-${it.id}`}>
                    <div className="flex items-start justify-between gap-2">
                      <p className={`font-body ${it.read ? "text-ink2" : "text-ink font-semibold"} text-[14px] truncate`}>
                        {it.name}
                      </p>
                      {!it.read && <span className="w-2 h-2 rounded-full bg-magenta shrink-0 mt-2" aria-label="unread" />}
                    </div>
                    <p className="text-[12.5px] text-muteink truncate mt-0.5">{it.organisation || it.email}</p>
                    <p className="text-[12px] text-muteink/80 truncate mt-1">{it.message}</p>
                    <p className="text-[11px] text-muteink/70 mt-1.5">{fmt(it.created_at)}</p>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Detail */}
        <div className="bg-white border border-blush-200/70 rounded-2xl p-6 md:p-8">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-muteink">
              <div className="text-center">
                <Mail className="mx-auto text-muteink/40 mb-3" size={28} />
                <p>Select an enquiry to read it.</p>
              </div>
            </div>
          ) : (
            <div data-testid="inbox-detail">
              <div className="flex flex-wrap items-start justify-between gap-3 pb-5 border-b border-blush-200/60">
                <div>
                  <p className="font-serif text-2xl text-ink">{selected.name}</p>
                  <p className="text-[13px] text-muteink mt-0.5">{selected.organisation || "—"}</p>
                </div>
                <div className="flex gap-2">
                  <a href={`mailto:${selected.email}?subject=Re:%20See%20Chelle%20enquiry`}
                     className="inline-flex items-center gap-1.5 text-[13px] bg-magenta text-white rounded-full px-3.5 py-1.5 hover:bg-magenta/90 transition-colors"
                     data-testid="inbox-reply-btn">
                    <Reply size={13} /> Reply
                  </a>
                  <button onClick={() => toggleRead(selected)} className="inline-flex items-center gap-1.5 text-[13px] text-ink2 hover:text-ink border border-blush-200 rounded-full px-3.5 py-1.5" data-testid="inbox-toggle-read-btn">
                    <MailOpen size={13} /> Mark {selected.read ? "unread" : "read"}
                  </button>
                  <button onClick={() => remove(selected)} className="inline-flex items-center gap-1.5 text-[13px] text-muteink hover:text-magenta border border-blush-200 rounded-full px-3.5 py-1.5" data-testid="inbox-delete-btn">
                    <Trash2 size={13} /> Delete
                  </button>
                </div>
              </div>

              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-4 mt-5 text-[14px]">
                <Row label="Email" value={<a href={`mailto:${selected.email}`} className="sc-link">{selected.email}</a>} />
                <Row label="Phone" value={selected.phone || "—"} />
                <Row label="How I can help" value={selected.help_with || "—"} />
                <Row label="Received" value={fmt(selected.created_at)} />
              </dl>

              <div className="mt-6">
                <p className="text-[11px] uppercase tracking-[0.22em] text-muteink font-semibold mb-2">Message</p>
                <div className="bg-blush-50 border border-blush-200/60 rounded-xl p-4 text-[15px] text-ink2 leading-[1.7] whitespace-pre-wrap">
                  {selected.message}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const Row = ({ label, value }) => (
  <div>
    <p className="text-[11px] uppercase tracking-[0.22em] text-muteink font-semibold mb-1">{label}</p>
    <p className="text-ink font-body">{value}</p>
  </div>
);

export default AdminInbox;
